import React, { useState, useEffect, useMemo } from 'react';
import UserInfo from '../components/UserProfile/UserInfo';
import BrokerLinking from '../components/UserProfile/BrokerLinking';
import LogsViewer from '../components/UserProfile/LogsViewer';
import Modal from '../components/common/Modal';
import X from '../components/common/X';
import VirtualAccountModal from '../components/Dashboard/VirtualAccountModal';
import AccountInstrumentsModal from '../components/Dashboard/AccountInstrumentsModal';
import {
  fetchUserProfile,
  fetchScannerLibrary,
  fetchSupportedBrokers,
  fetchVirtualAccounts,
  toggleVirtualAccountSync,
  deleteVirtualAccount,
  setPriority,
  linkBroker,
  unlinkBroker,
  fetchAssets,
  updateVirtualAccountSyncSettings,
} from '../services/api';
import { truncateText } from '../utils/helpers';
import ProfileKPIStatistic from '../components/Dashboard/ProfileKPIStatistic';
import VirtualAccountSyncSettings from '../components/Dashboard/VirtualAccountSyncSettings';
import NumberDropdown from '../components/Dropdown';
import theme from '../theme';

function UserProfilePage() {
  const [userProfile, setUserProfile] = useState(null);
  const [supportedBrokers, setSupportedBrokers] = useState([]);
  const [virtualAccounts, setVirtualAccounts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isVirtualModalOpen, setIsVirtualModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [broker, setBroker] = useState('');
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [syncLoading, setSyncLoading] = useState({});
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmVirtualAccount, setDeleteConfirmVirtualAccount] = useState(null);
  const [selectedVirtualAccountForSettings, setSelectedVirtualAccountForSettings] = useState(null);
  const [showSyncSettingsModal, setShowSyncSettingsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [selectedBroker, setSelectedBroker] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [showStatistics, setShowStatistics] = useState(false);
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [entries, setEntries] = useState([]);
  const [accountOperation, setAccountOperation] = useState(null);
  const [deleteAccount, setDeleteAccount] = useState(null);
  const [accountError, setAccountError] = useState('');

  const selectedSymbolSectypes = useMemo(
    () =>
      selectedSymbols.map((symbolName) => {
        const asset = assets.find((entry) => (entry.symbol || entry.crypto) === symbolName);
        return asset?.sectype || null;
      }),
    [assets, selectedSymbols],
  );

  const handleSetPriority = async (recordId, priority) => {
    setEntries((prev) =>
      prev.map((entry) =>
        entry.record_id === recordId ? { ...entry, priority } : entry,
      ),
    );

    try {
      await setPriority(recordId, priority);
    } catch (err) {
      console.log('Error set priority:', err);
    }
  };

  const loadAssets = async (accountToLoad, brokerToLoad) => {
    setLoadingAssets(true);
    try {
      const assetsList = await fetchAssets(accountToLoad, brokerToLoad);
      setAssets(assetsList || []);
    } catch (err) {
      console.error('Error loading assets:', err);
      setAssets([]);
    } finally {
      setLoadingAssets(false);
    }
  };

  const handleSelectBroker = async (brokerData) => {
    setSelectedBroker(brokerData.broker);
    setSelectedAccount(brokerData.account);
    setSelectedSymbols([]);
    await loadAssets(brokerData.account, brokerData.broker);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const result = await linkBroker(broker, account, password, apiKey);
      if (result) {
        setIsModalOpen(false);
        window.location.reload();
      } else {
        setError('Something went wrong..');
      }
    } catch (err) {
      setError('Something went wrong..');
    }
  };

  const handleUnlink = async (recordId) => {
    setAccountError('');
    setDeleteAccount(userProfile.linkedBrokers.find((entry) => entry.record_id === recordId));
  };

  const confirmUnlink = async () => {
    setDeleteLoading(true);
    try {
      const result = await unlinkBroker(deleteAccount.record_id);
      if (result) {
        setDeleteAccount(null);
        setIsModalOpen(false);
        const profile = await fetchUserProfile();
        setUserProfile(profile);
        setEntries(profile.linkedBrokers || []);
        await loadVirtualAccounts();
      } else {
        setAccountError('Could not delete the account.');
      }
    } catch (err) {
      setAccountError(err.response?.data?.detail || 'Could not delete the account.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openAccountInstruments = async (entry) => {
    setAccountError('');
    try {
      const library = await fetchScannerLibrary();
      if (!library) throw new Error('No library');
      setIsModalOpen(false);
      setAccountOperation({ library, sourceAccount: entry.account });
    } catch (err) {
      setAccountError('Could not load account instruments.');
    }
  };

  const handleDeleteVirtualAccount = async (virtualAccountId) => {
    setDeleteLoading(true);
    try {
      const result = await deleteVirtualAccount(virtualAccountId);
      if (result.success) {
        setVirtualAccounts(virtualAccounts.filter((va) => va.id !== virtualAccountId));
        setDeleteConfirmVirtualAccount(null);
      } else {
        console.error('Failed to delete virtual account:', result.message);
      }
    } catch (deleteError) {
      console.error('Error deleting virtual account:', deleteError);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleSync = async (virtualAccountId, currentSyncState) => {
    setSyncLoading((prev) => ({ ...prev, [virtualAccountId]: true }));
    try {
      const result = await toggleVirtualAccountSync(virtualAccountId, !currentSyncState);
      if (result) {
        setVirtualAccounts(
          virtualAccounts.map((va) =>
            va.id === virtualAccountId
              ? { ...va, sync_enabled: !currentSyncState }
              : va,
          ),
        );
      }
    } catch (err) {
      console.error('Error toggling sync:', err);
    } finally {
      setSyncLoading((prev) => ({ ...prev, [virtualAccountId]: false }));
    }
  };

  const handleVirtualAccountCreated = async () => {
    await loadVirtualAccounts();
    setIsVirtualModalOpen(false);
  };

  const loadVirtualAccounts = async () => {
    try {
      const accounts = await fetchVirtualAccounts();
      setVirtualAccounts(accounts || []);
    } catch (err) {
      console.error('Error loading virtual accounts:', err);
    }
  };

  useEffect(() => {
    const loadUserProfile = async () => {
      const profile = await fetchUserProfile();
      setUserProfile(profile);
    };

    const loadSupportedBrokers = async () => {
      const brokers = await fetchSupportedBrokers();
      setSupportedBrokers(brokers);
    };

    loadUserProfile();
    loadSupportedBrokers();
    loadVirtualAccounts();
  }, []);

  if (!userProfile) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <div className={`w-8 h-8 border-2 ${theme.spinner} rounded-full animate-spin`} />
      </div>
    );
  }

  const tabClass = (tab) =>
    `py-2.5 px-4 text-sm font-semibold rounded-xl transition-all duration-200 ${
      activeTab === tab ? `${theme.pillActive}` : `${theme.pillInactive}`
    }`;

  return (
    <div className={`min-h-screen ${theme.pageBg}`}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className={`text-2xl font-bold ${theme.textPrimary} tracking-wide mb-6`}>Profile</h1>

        <div className="mb-6">
          <nav className={`inline-flex gap-1.5 ${theme.pillContainer}`}>
            <button onClick={() => setActiveTab('profile')} className={tabClass('profile')}>
              Profile & Accounts
            </button>
            <button onClick={() => setActiveTab('logs')} className={tabClass('logs')}>
              Trading Logs
            </button>
          </nav>
        </div>

        {activeTab === 'profile' && (
          <div className="space-y-6">
            <UserInfo user={userProfile} />
            <BrokerLinking
              supportedBrokers={supportedBrokers}
              linkedBrokers={userProfile.linkedBrokers}
              onLinkBroker={(selectedBrokerName) => {
                setIsModalOpen(true);
                setBroker(selectedBrokerName);
              }}
              onOpenVirtualModal={() => setIsVirtualModalOpen(true)}
            />

            <section className={`${theme.cardBg} ${theme.cardRound} ${theme.cardBorder} p-5`}>
              <h2 className={`mb-3 text-sm font-bold ${theme.textPrimary}`}>Linked accounts</h2>
              {accountError && !deleteAccount && <p role="alert" className="mb-2 text-xs text-red-600">{accountError}</p>}
              <p className={`mb-3 text-xs ${theme.textMuted}`}>Move or detach instruments before deleting an account. Only empty accounts can be deleted.</p>
              <div className="space-y-2">
                {(userProfile.linkedBrokers || []).map((entry) => (
                  <div key={entry.record_id} className="flex flex-wrap items-center gap-3 rounded-lg bg-olive-50 p-3 text-xs">
                    <span className="mr-auto font-semibold">{entry.broker.toUpperCase()} · {truncateText(entry.account, 18)}</span>
                    {entry.broker === 'ib' && <button className="font-bold text-olive-700" onClick={() => openAccountInstruments(entry)}>Manage instruments</button>}
                    <button className="text-red-600" onClick={() => handleUnlink(entry.record_id)}>Delete account</button>
                  </div>
                ))}
              </div>
            </section>

            {virtualAccounts.length > 0 && (
              <div
                className={`${theme.cardBg} ${theme.cardRound} ${theme.cardBorder} p-5`}
                style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}
              >
                <h2 className={`text-sm font-bold ${theme.textPrimary} mb-4`}>Virtual Accounts</h2>
                <div className="space-y-3">
                  {virtualAccounts.map((va) => (
                    <div key={va.id} className={`rounded-xl border border-olive-200 ${theme.sectionBg} p-4`}>
                      <div className="flex justify-between items-center mb-3">
                        <h3 className={`text-sm font-bold ${theme.textPrimary}`}>{va.name}</h3>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedVirtualAccountForSettings(va);
                              setShowSyncSettingsModal(true);
                            }}
                            className={`p-1.5 ${theme.textMuted} hover:text-olive-700 transition-colors rounded-lg hover:bg-olive-50`}
                            title="Sync settings"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                          <span className={`text-xs ${theme.textMuted} font-medium`}>
                            Sync {va.sync_enabled ? 'ON' : 'OFF'}
                          </span>
                          <button
                            onClick={() => handleToggleSync(va.id, va.sync_enabled)}
                            disabled={syncLoading[va.id]}
                            className={`relative inline-flex items-center h-5 rounded-full w-9 transition-colors duration-200 ${va.sync_enabled ? 'bg-emerald-500' : 'bg-olive-300'}`}
                          >
                            <span className={`${va.sync_enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'} inline-block w-3.5 h-3.5 transform bg-white rounded-full transition-transform shadow-sm`} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirmVirtualAccount(va)}
                            className={`p-1.5 ${theme.textMuted} hover:text-red-500 transition-colors rounded-lg hover:bg-red-50`}
                            title="Delete virtual account"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <p className={`text-xs ${theme.textMuted} mb-2`}>{va.linked_accounts.length} linked accounts</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {va.linked_accounts.map((linkedAccount) => (
                          <div
                            key={linkedAccount.id}
                            className={`py-2 px-3 rounded-lg text-sm ${
                              linkedAccount.is_source
                                ? 'bg-olive-100 border border-olive-400'
                                : `${theme.sectionBg} border border-olive-200`
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className={`text-xs font-medium ${theme.textPrimary}`}>
                                {truncateText(linkedAccount.account, 15)}
                              </span>
                              {linkedAccount.is_source && (
                                <span className={`text-[10px] ${theme.badgeActive} px-1.5 py-0.5 rounded-md font-semibold`}>
                                  Source
                                </span>
                              )}
                            </div>
                            <div className={`text-[10px] ${theme.textMuted} mt-0.5`}>
                              {linkedAccount.broker.toUpperCase()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div
              className={`${theme.cardBg} ${theme.cardRound} ${theme.cardBorder} p-5`}
              style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.04)' }}
            >
              {!showStatistics ? (
                <div>
                  <h2 className={`text-sm font-bold ${theme.textPrimary} mb-4`}>Trading Statistics</h2>
                  <div className="max-w-lg mx-auto space-y-5">
                    <div>
                      <label className={`block text-xs font-semibold ${theme.textSecondary} mb-2 tracking-wide uppercase`}>
                        1. Select Broker
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {userProfile?.linkedBrokers?.map((brokerData) => (
                          <button
                            key={brokerData.record_id}
                            onClick={() => handleSelectBroker(brokerData)}
                            className={`p-2.5 text-sm rounded-xl border transition-all duration-200 ${
                              selectedBroker === brokerData.broker && selectedAccount === brokerData.account
                                ? `${theme.btnPrimary} border-olive-600 shadow-sm`
                                : `${theme.sectionBg} ${theme.textPrimary} border-olive-200 hover:bg-olive-100 hover:border-olive-300`
                            }`}
                          >
                            {brokerData.broker.toUpperCase()}
                            <div className="text-[10px] opacity-75 mt-0.5">
                              {truncateText(brokerData.account, 10)}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className={`block text-xs font-semibold ${theme.textSecondary} mb-2 tracking-wide uppercase`}>
                        2. Select Symbols ({selectedSymbols.length} selected)
                      </label>

                      {selectedSymbols.length > 0 && (
                        <div className="mb-3 p-2.5 bg-olive-50 rounded-xl border border-olive-200">
                          <div className="text-[10px] text-olive-600 mb-1 font-semibold uppercase tracking-wider">
                            Selected:
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {selectedSymbols.map((selectedSymbol) => (
                              <span
                                key={selectedSymbol}
                                className={`inline-flex items-center px-2 py-0.5 ${theme.badgeActive} text-xs rounded-lg font-medium`}
                              >
                                {selectedSymbol}
                                <button
                                  type="button"
                                  aria-label={`Remove ${selectedSymbol}`}
                                  onClick={() =>
                                    setSelectedSymbols((prev) => prev.filter((symbolName) => symbolName !== selectedSymbol))
                                  }
                                  className="ml-1 hover:text-olive-200"
                                >
                                  x
                                </button>
                              </span>
                            ))}
                          </div>
                          <button
                            onClick={() => setSelectedSymbols([])}
                            className="text-[10px] text-olive-600 hover:text-olive-800 mt-1 font-medium"
                          >
                            Clear all
                          </button>
                        </div>
                      )}

                      {loadingAssets ? (
                        <div className="text-center p-4">
                          <div className={`inline-block w-4 h-4 border-2 ${theme.spinner} rounded-full animate-spin`} />
                          <span className={`ml-2 text-xs ${theme.textMuted}`}>Loading symbols...</span>
                        </div>
                      ) : assets.length > 0 ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto p-1">
                          {assets.map((asset, index) => {
                            const symbolName = asset.symbol || asset.crypto;
                            const isSelected = selectedSymbols.includes(symbolName);

                            return (
                              <button
                                key={index}
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedSymbols((prev) => prev.filter((entry) => entry !== symbolName));
                                  } else {
                                    setSelectedSymbols((prev) => [...prev, symbolName]);
                                  }
                                }}
                                className={`p-2 text-xs rounded-lg border truncate transition-all duration-200 ${
                                  isSelected
                                    ? `${theme.btnPrimary} border-olive-600`
                                    : `${theme.sectionBg} ${theme.textPrimary} border-olive-200 hover:bg-olive-100 hover:border-olive-300`
                                }`}
                              >
                                {symbolName}
                                {isSelected && <span className="ml-1">selected</span>}
                              </button>
                            );
                          })}
                        </div>
                      ) : selectedBroker ? (
                        <div className={`text-xs ${theme.textMuted} text-center p-4 border border-dashed border-olive-300 rounded-xl`}>
                          No assets found for this broker account
                        </div>
                      ) : (
                        <div className={`text-xs ${theme.textMuted} text-center p-4 border border-dashed border-olive-300 rounded-xl`}>
                          Select a broker first
                        </div>
                      )}
                    </div>

                    <button
                      onClick={() => setShowStatistics(true)}
                      disabled={!selectedBroker || selectedSymbols.length === 0}
                      className={`w-full py-2.5 px-4 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-[0.98] ${
                        !selectedBroker || selectedSymbols.length === 0
                          ? 'bg-olive-200 text-olive-400 cursor-not-allowed'
                          : theme.btnPrimary
                      }`}
                    >
                      Show Statistics ({selectedSymbols.length} symbols)
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className={`text-sm font-bold ${theme.textPrimary}`}>
                      Statistics: {selectedBroker.toUpperCase()} - {truncateText(selectedAccount, 10)}
                    </h2>
                    <button
                      onClick={() => setShowStatistics(false)}
                      className="text-xs text-olive-600 hover:text-olive-800 font-medium"
                    >
                      &lt; Back
                    </button>
                  </div>
                  <ProfileKPIStatistic
                    broker={selectedBroker}
                    account={selectedBroker === 'okx' ? userProfile?.name : selectedAccount}
                    symbols={selectedSymbols}
                    symbolSectypes={selectedSymbolSectypes}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'logs' && <LogsViewer />}

        {accountOperation && <AccountInstrumentsModal library={accountOperation.library}
          accounts={userProfile.linkedBrokers || []} sourceAccount={accountOperation.sourceAccount}
          onClose={() => setAccountOperation(null)} onChanged={async () => {
            const library = await fetchScannerLibrary();
            setAccountOperation((prev) => prev ? { ...prev, library } : null);
          }} />}

        {deleteAccount && <Modal open onClose={() => { if (!deleteLoading) setDeleteAccount(null); }}>
          <section role="dialog" aria-modal="true" aria-label="Delete linked account" className="max-w-sm space-y-3">
            <h2 className="text-sm font-bold">Delete account {truncateText(deleteAccount.account, 18)}?</h2>
            <p className="text-xs">The account must have no instruments. This removes its link to the app. Positions and the account at the broker remain unchanged.</p>
            {accountError && <p role="alert" className="text-xs text-red-600">{accountError}</p>}
            <button className="mr-3 text-xs" disabled={deleteLoading} onClick={() => setDeleteAccount(null)}>Cancel</button>
            <button className="text-xs font-bold text-red-600" disabled={deleteLoading} onClick={confirmUnlink}>
              {deleteLoading ? 'Deleting…' : 'Delete empty account'}
            </button>
          </section>
        </Modal>}

        <Modal open={isModalOpen && !deleteAccount} onClose={() => setIsModalOpen(false)}>
          <div className="text-center w-72">
            <div className="mx-auto my-4 w-64">
              {userProfile.linkedBrokers
                .filter((entry) => entry.broker === broker)
                .map((entry) => (
                  <div key={entry.record_id} className={`${theme.cardBg} rounded-xl ${theme.cardBorder} overflow-visible my-1`}>
                    <ul className="divide-y divide-olive-100">
                      <li className="p-3 flex justify-between items-center gap-3">
                        <span className={`text-sm font-medium ${theme.textPrimary}`}>
                          {truncateText(entry.account, 15)}
                        </span>
                        <div className="overflow-visible">
                          <NumberDropdown
                            initialValue={entry.priority}
                            onSelect={(priority) => handleSetPriority(entry.record_id, priority)}
                          />
                        </div>
                        <button
                          className={`${theme.textMuted} hover:text-red-500 transition-colors`}
                          onClick={() => handleUnlink(entry.record_id)}
                        >
                          <X />
                        </button>
                      </li>
                    </ul>
                  </div>
                ))}

              <h3 className={`text-sm font-bold ${theme.textPrimary} mt-4 mb-2`}>
                Enter {broker.toUpperCase()} credentials
              </h3>
              {error && <p className="text-red-500 mb-3 text-xs">{error}</p>}
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label htmlFor="account" className={`block mb-1 text-xs font-semibold ${theme.textSecondary} text-left tracking-wide uppercase`}>
                    {broker === 'ib' ? 'Account' : broker === 'okx' ? 'Secret Key' : ''}
                  </label>
                  <input
                    type="text"
                    id="account"
                    value={account}
                    onChange={(event) => setAccount(event.target.value)}
                    className={`w-full px-3 py-2 text-sm ${theme.input}`}
                    required
                  />
                </div>
                {broker === 'okx' && (
                  <>
                    <div>
                      <label htmlFor="password" className={`block mb-1 text-xs font-semibold ${theme.textSecondary} text-left tracking-wide uppercase`}>
                        Passphrase
                      </label>
                      <input
                        type="password"
                        id="password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        className={`w-full px-3 py-2 text-sm ${theme.input}`}
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="apiKey" className={`block mb-1 text-xs font-semibold ${theme.textSecondary} text-left tracking-wide uppercase`}>
                        API Key
                      </label>
                      <input
                        type="text"
                        id="apiKey"
                        value={apiKey}
                        onChange={(event) => setApiKey(event.target.value)}
                        className={`w-full px-3 py-2 text-sm ${theme.input}`}
                      />
                    </div>
                  </>
                )}
                <button type="submit" className={`w-full py-2.5 px-4 rounded-xl text-sm font-semibold ${theme.btnPrimary}`}>
                  Submit
                </button>
              </form>
            </div>
          </div>
        </Modal>

        <VirtualAccountModal
          open={isVirtualModalOpen}
          onClose={() => setIsVirtualModalOpen(false)}
          linkedBrokers={userProfile.linkedBrokers}
          onSuccess={handleVirtualAccountCreated}
        />

        <Modal open={deleteConfirmVirtualAccount !== null} onClose={() => setDeleteConfirmVirtualAccount(null)}>
          <div className={`text-center p-6 max-w-sm mx-auto ${theme.cardBg} ${theme.cardRound} ${theme.cardBorder}`}>
            <svg className="mx-auto h-10 w-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className={`text-sm font-bold ${theme.textPrimary} mt-4`}>Delete Virtual Account</h3>
            <div className={`mt-2 text-xs ${theme.textMuted}`}>
              <p>
                Are you sure you want to delete
                <span className={`font-bold ${theme.textPrimary} block mt-1`}>
                  {deleteConfirmVirtualAccount?.name}
                </span>
              </p>
              <p className="mt-1.5">This action cannot be undone.</p>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button
                onClick={() => setDeleteConfirmVirtualAccount(null)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold ${theme.btnSecondary}`}
              >
                Cancel
              </button>
              <button
                disabled={deleteLoading}
                className={`px-4 py-2 rounded-xl text-sm font-semibold ${theme.btnDanger} disabled:opacity-50`}
                onClick={() => handleDeleteVirtualAccount(deleteConfirmVirtualAccount.id)}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>

        <Modal
          open={showSyncSettingsModal}
          onClose={() => {
            setShowSyncSettingsModal(false);
            setSelectedVirtualAccountForSettings(null);
          }}
        >
          <div className="max-w-2xl mx-auto">
            {selectedVirtualAccountForSettings && (
              <>
                <h2 className={`text-sm font-bold ${theme.textPrimary} mb-4`}>
                  Sync Settings: {selectedVirtualAccountForSettings.name}
                </h2>
                <VirtualAccountSyncSettings
                  virtualAccountId={selectedVirtualAccountForSettings.id}
                  onSettingsUpdate={async (newSettings) => {
                    try {
                      await updateVirtualAccountSyncSettings(selectedVirtualAccountForSettings.id, newSettings);
                      await loadVirtualAccounts();
                    } catch (settingsError) {
                      console.error('Error updating sync settings:', settingsError);
                    }
                  }}
                />
              </>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
}

export default UserProfilePage;
